import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Video } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

const VideoShowcaseManagement = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [formData, setFormData] = useState({
    videoId: '',
    videoType: 'youtube', // youtube or url
    videoUrl: '',
    engineered: '',
    directed: '',
    shot: '',
    produced: ''
  });

  // Load videos on mount
  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/showcase-videos`);
      setVideos(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
      setLoading(false);
    }
  };

  const handleAddVideo = async () => {
    if (!formData.videoId && !formData.videoUrl) {
      toast.error('Please provide either a YouTube Video ID or URL');
      return;
    }

    // Extract YouTube ID if a URL was provided
    let finalVideoId = formData.videoId;
    if (formData.videoType === 'youtube' && formData.videoId) {
      // Try to extract ID from URL if user pasted a full URL
      const extractedId = extractYouTubeId(formData.videoId);
      if (extractedId) {
        finalVideoId = extractedId;
      }
      
      // Validate the final ID format
      const youtubeRegex = /^[a-zA-Z0-9_-]{11}$/;
      if (!youtubeRegex.test(finalVideoId)) {
        toast.error('Invalid YouTube Video ID. Please check the URL or ID and try again.');
        return;
      }
    }

    try {
      const videoData = {
        videoId: formData.videoType === 'youtube' ? finalVideoId : '',
        videoUrl: formData.videoType === 'url' ? formData.videoUrl : '',
        videoType: formData.videoType,
        thumbnail: formData.videoType === 'youtube' 
          ? `https://img.youtube.com/vi/${finalVideoId}/maxresdefault.jpg`
          : '',
        engineered: formData.engineered,
        directed: formData.directed,
        shot: formData.shot,
        produced: formData.produced
      };

      const response = await axios.post(`${API}/showcase-videos`, videoData);
      setVideos([...videos, response.data]);
      toast.success('Video added successfully!');
      
      // Reset form
      setFormData({
        videoId: '',
        videoType: 'youtube',
        videoUrl: '',
        engineered: '',
        directed: '',
        shot: '',
        produced: ''
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding video:', error);
      toast.error('Failed to add video');
    }
  };

  const handleUpdateVideo = async (id) => {
    try {
      const video = videos.find(v => v.id === id);
      const response = await axios.put(`${API}/showcase-videos/${id}`, video);
      toast.success('Video updated successfully!');
      setEditingId(null);
      fetchVideos();
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error('Failed to update video');
    }
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await axios.delete(`${API}/showcase-videos/${id}`);
      setVideos(videos.filter(v => v.id !== id));
      toast.success('Video deleted successfully!');
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };

  const handleEditVideo = (id, field, value) => {
    setVideos(videos.map(v => 
      v.id === id ? { ...v, [field]: value } : v
    ));
  };

  const extractYouTubeId = (url) => {
    // Handle various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/, // Standard watch URL
      /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/, // Embed URL
      /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/, // Short URL
      /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/, // Old embed
      /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/, // Shorts
      /^([a-zA-Z0-9_-]{11})$/ // Just the ID
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  const handleYouTubeUrlPaste = (e) => {
    const pastedUrl = e.clipboardData.getData('text');
    const videoId = extractYouTubeId(pastedUrl);
    if (videoId) {
      setFormData({ ...formData, videoId: videoId });
      toast.success('YouTube Video ID extracted!');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index.toString());
  };

  const handleDragEnd = (e) => {
    setDraggedIndex(null);
    // Remove all drag-over styling
    document.querySelectorAll('.drag-over').forEach(el => {
      el.classList.remove('drag-over');
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Add visual feedback
    if (e.currentTarget.classList) {
      e.currentTarget.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.classList) {
      e.currentTarget.classList.remove('drag-over');
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Remove drag-over styling
    if (e.currentTarget.classList) {
      e.currentTarget.classList.remove('drag-over');
    }
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newVideos = [...videos];
    const draggedVideo = newVideos[draggedIndex];
    
    // Remove from old position
    newVideos.splice(draggedIndex, 1);
    
    // Adjust drop index if dragging from before the drop position
    const adjustedDropIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
    
    // Insert at new position
    newVideos.splice(adjustedDropIndex, 0, draggedVideo);
    
    setVideos(newVideos);
    toast.success('Video order updated!');
    
    // You can optionally save the new order to the backend here
    // saveVideoOrder(newVideos);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <style>{`
        .drag-over {
          border-color: rgb(168, 85, 247) !important;
          background-color: rgba(168, 85, 247, 0.1) !important;
          transform: scale(1.02);
        }
      `}</style>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-white">Video Showcase Management</h3>
          <p className="text-gray-400 text-sm mt-1">Manage videos displayed in the Studio Showcase section</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center space-x-2"
        >
          {showAddForm ? <X size={20} /> : <Plus size={20} />}
          <span>{showAddForm ? 'Cancel' : 'Add Video'}</span>
        </button>
      </div>

      {/* Add Video Form */}
      {showAddForm && (
        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-6 space-y-4">
          <h4 className="text-lg font-semibold text-white mb-4">Add New Video</h4>
          
          {/* Video Type Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Video Source</label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="youtube"
                  checked={formData.videoType === 'youtube'}
                  onChange={(e) => setFormData({ ...formData, videoType: e.target.value })}
                  className="text-purple-600"
                />
                <span className="text-gray-300">YouTube Video</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="url"
                  checked={formData.videoType === 'url'}
                  onChange={(e) => setFormData({ ...formData, videoType: e.target.value })}
                  className="text-purple-600"
                />
                <span className="text-gray-300">Direct URL</span>
              </label>
            </div>
          </div>

          {/* Video ID/URL Input */}
          {formData.videoType === 'youtube' ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                YouTube Video ID or URL
                <span className="text-xs text-gray-500 ml-2">(Paste full URL or just the ID)</span>
              </label>
              <input
                type="text"
                placeholder="e.g., dQw4w9WgXcQ or paste full YouTube URL"
                value={formData.videoId}
                onChange={(e) => setFormData({ ...formData, videoId: e.target.value })}
                onPaste={handleYouTubeUrlPaste}
                className="w-full px-4 py-2 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
              {formData.videoId && (
                <p className="text-xs text-gray-400">
                  Preview: https://youtube.com/watch?v={formData.videoId}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Video URL</label>
              <input
                type="url"
                placeholder="https://example.com/video.mp4"
                value={formData.videoUrl}
                onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                className="w-full px-4 py-2 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>
          )}

          {/* Credits Section */}
          <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-purple-500/20">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Engineered By</label>
              <input
                type="text"
                placeholder="e.g., Trap Spade"
                value={formData.engineered}
                onChange={(e) => setFormData({ ...formData, engineered: e.target.value })}
                className="w-full px-4 py-2 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Directed By</label>
              <input
                type="text"
                placeholder="Optional"
                value={formData.directed}
                onChange={(e) => setFormData({ ...formData, directed: e.target.value })}
                className="w-full px-4 py-2 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Shot By</label>
              <input
                type="text"
                placeholder="Optional"
                value={formData.shot}
                onChange={(e) => setFormData({ ...formData, shot: e.target.value })}
                className="w-full px-4 py-2 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Produced By</label>
              <input
                type="text"
                placeholder="Optional"
                value={formData.produced}
                onChange={(e) => setFormData({ ...formData, produced: e.target.value })}
                className="w-full px-4 py-2 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>
          </div>

          <button
            onClick={handleAddVideo}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 transition-opacity font-semibold"
          >
            Add Video
          </button>
        </div>
      )}

      {/* Videos List */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-white">Current Videos ({videos.length})</h4>
        
        {videos.length === 0 ? (
          <div className="bg-purple-900/10 border border-purple-500/20 rounded-lg p-8 text-center">
            <Video size={48} className="mx-auto text-gray-500 mb-4" />
            <p className="text-gray-400">No videos yet. Add your first video to get started!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {videos.map((video, index) => (
              <div
                key={video.id}
                draggable={true}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                style={{ opacity: draggedIndex === index ? 0.5 : 1 }}
                className="bg-purple-900/20 border-2 border-purple-500/30 rounded-lg p-2 space-y-2 cursor-move hover:border-purple-500/60 transition-all duration-200"
              >
                {/* Video Preview - Small Square thumbnail */}
                {video.videoType === 'youtube' && video.videoId && (
                  <div className="aspect-square rounded-lg overflow-hidden bg-black">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.videoId}`}
                      className="w-full h-full"
                      allowFullScreen
                      title="Video preview"
                    ></iframe>
                  </div>
                )}

                {/* Credits */}
                {editingId === video.id ? (
                  <div className="space-y-1">
                    <input
                      type="text"
                      placeholder="Engineered By"
                      value={video.engineered || ''}
                      onChange={(e) => handleEditVideo(video.id, 'engineered', e.target.value)}
                      className="w-full px-2 py-1 bg-black/40 border border-purple-500/20 rounded text-white text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Directed By"
                      value={video.directed || ''}
                      onChange={(e) => handleEditVideo(video.id, 'directed', e.target.value)}
                      className="w-full px-2 py-1 bg-black/40 border border-purple-500/20 rounded text-white text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Shot By"
                      value={video.shot || ''}
                      onChange={(e) => handleEditVideo(video.id, 'shot', e.target.value)}
                      className="w-full px-2 py-1 bg-black/40 border border-purple-500/20 rounded text-white text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Produced By"
                      value={video.produced || ''}
                      onChange={(e) => handleEditVideo(video.id, 'produced', e.target.value)}
                      className="w-full px-2 py-1 bg-black/40 border border-purple-500/20 rounded text-white text-xs"
                    />
                  </div>
                ) : (
                  <div className="space-y-0.5 text-xs">
                    {video.engineered && (
                      <p className="text-gray-300 truncate">
                        <span className="text-purple-400 font-semibold">Eng:</span> {video.engineered}
                      </p>
                    )}
                    {video.directed && (
                      <p className="text-gray-300 truncate">
                        <span className="text-purple-400 font-semibold">Dir:</span> {video.directed}
                      </p>
                    )}
                    {video.shot && (
                      <p className="text-gray-300 truncate">
                        <span className="text-purple-400 font-semibold">Shot:</span> {video.shot}
                      </p>
                    )}
                    {video.produced && (
                      <p className="text-gray-300 truncate">
                        <span className="text-purple-400 font-semibold">Prod:</span> {video.produced}
                      </p>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center space-x-1 pt-2 border-t border-purple-500/20">
                  {editingId === video.id ? (
                    <>
                      <button
                        onClick={() => handleUpdateVideo(video.id)}
                        className="flex-1 px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center justify-center space-x-1 text-xs"
                      >
                        <Save size={14} />
                        <span className="hidden sm:inline">Save</span>
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="flex-1 px-2 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors flex items-center justify-center space-x-1 text-xs"
                      >
                        <X size={14} />
                        <span className="hidden sm:inline">Cancel</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setEditingId(video.id)}
                        className="flex-1 px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1 text-xs"
                      >
                        <Edit2 size={14} />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        onClick={() => handleDeleteVideo(video.id)}
                        className="flex-1 px-2 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center justify-center space-x-1 text-xs"
                      >
                        <Trash2 size={14} />
                        <span className="hidden sm:inline">Del</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoShowcaseManagement;
